import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import api from '../utils/api';
import { useCart } from '../context/CartContext';
import { formatPKR } from '../utils/formatters';
import { FiShoppingBag, FiHeart, FiShare2, FiTruck, FiShield, FiRotateCw } from 'react-icons/fi';
import ProductCard from '../components/common/ProductCard';
import ViewingCounter from '../components/common/ViewingCounter';
import SaleTimer from '../components/common/SaleTimer';
import ProductImage from '../components/ProductImage';


const ProductDetail = () => {
  const { id } = useParams();
  const { addToCart } = useCart();
  const [product, setProduct] = useState(null);
  const [relatedProducts, setRelatedProducts] = useState([]);
  const [selectedImage, setSelectedImage] = useState(0);
  const [selectedSize, setSelectedSize] = useState('');
  const [selectedColor, setSelectedColor] = useState('');
  const [loading, setLoading] = useState(true);
  const [quantity, setQuantity] = useState(1);

  useEffect(() => {
    loadProduct();
  }, [id]);

  useEffect(() => {
    if (product) {
      document.title = `${product.name} | TRENDZ`;
      const meta = document.querySelector('meta[name="description"]');
      if (meta) meta.setAttribute('content', product.description.slice(0, 160));
    }
  }, [product]);

  const loadProduct = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/products/${id}`);
      setProduct(data.product);

      if (data.product) {
        // Meta Pixel Tracking
        if (window.fbq) {
          window.fbq('track', 'ViewContent', {
            content_name: data.product.name,
            content_category: data.product.category,
            content_ids: [data.product._id],
            content_type: 'product',
            value: data.product.salePrice || data.product.price,
            currency: 'PKR'
          });
        }

        // Load related products
        const relatedRes = await api.get(`/products/${data.product._id}/related`);
        setRelatedProducts(relatedRes.data.products || []);

        // Initial Selection
        if (data.product.sizes?.length) setSelectedSize(data.product.sizes[0]);
        if (data.product.colors?.length) setSelectedColor(data.product.colors[0]);
      }
    } catch (error) {
      console.error('Error loading product:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddToCart = () => {
    if (product) {
      addToCart(product, { size: selectedSize, color: selectedColor }, quantity);
    }
  };

  if (loading) {
    return (
      <div className="container mx-auto px-6 py-12">
        <div className="grid md:grid-cols-2 gap-12">
          <div className="skeleton h-[500px] rounded-2xl" />
          <div className="space-y-6">
            <div className="skeleton h-12 w-3/4 rounded" />
            <div className="skeleton h-8 w-1/4 rounded" />
            <div className="skeleton h-32 w-full rounded" />
          </div>
        </div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="container mx-auto px-6 py-24 text-center">
        <h2 className="text-2xl text-white font-bold">Product not found</h2>
        <Link to="/products" className="btn-secondary mt-4 inline-block">Back to Collection</Link>
      </div>
    );
  }

  const discountPercentage = product.salePrice
    ? Math.round(((product.price - product.salePrice) / product.price) * 100)
    : 0;

  return (
    <div className="min-h-screen bg-dark-bg text-gray-100">
      <div className="container mx-auto px-6 py-12">
        <div className="grid md:grid-cols-2 gap-12 lg:gap-20">

          {/* Image Gallery */}
          <div className="space-y-4">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="relative aspect-square rounded-2xl overflow-hidden border border-white/10 bg-dark-card"
            >
              {product.salePrice && (
                <span className="absolute top-4 left-4 bg-primary-light text-white px-4 py-1 rounded-full text-sm font-bold z-10 shadow-lg">
                  -{discountPercentage}% SALE
                </span>
              )}
              <ProductImage
                src={product.images && product.images[selectedImage] ? product.images[selectedImage] : ''}
                alt={product.name}
                className="w-full h-full"
              />

            </motion.div>

            <div className="grid grid-cols-4 gap-4">
              {product.images.map((img, idx) => (
                <button
                  key={idx}
                  onClick={() => setSelectedImage(idx)}
                  className={`relative aspect-square rounded-lg overflow-hidden border-2 transition-all ${selectedImage === idx ? 'border-primary-light' : 'border-transparent hover:border-gray-600'
                    }`}
                >
                  <ProductImage
                    src={img}
                    alt=""
                    className="w-full h-full"
                  />

                </button>
              ))}
            </div>
          </div>

          {/* Product Info */}
          <div className="flex flex-col justify-center">
            <div className="mb-8">
              <h1 className="text-4xl md:text-5xl font-display font-bold mb-4">{product.name}</h1>
              <div className="flex items-baseline gap-4 mb-2">
                {product.salePrice ? (
                  <>
                    <span className="text-3xl font-bold text-primary-light">{formatPKR(product.salePrice)}</span>
                    <span className="text-xl text-gray-500 line-through">{formatPKR(product.price)}</span>
                  </>
                ) : (
                  <span className="text-3xl font-bold">{formatPKR(product.price)}</span>
                )}
              </div>
              <div className="flex items-center gap-2 mb-4">
                <div className="flex text-yellow-500">
                  {[...Array(5)].map((_, i) => (
                    <span key={i}>{i < Math.round(product.ratings || 5) ? '★' : '☆'}</span>
                  ))}
                </div>
                <span className="text-sm text-gray-500">({product.numReviews || 5} reviews)</span>
              </div>
              <div className="mt-4 mb-6">
                {(product.salePrice || true) && <SaleTimer />}
                <ViewingCounter min={1} max={15} label="people looking at this gear right now" />
              </div>
              <p className="text-gray-400 leading-relaxed max-w-lg">
                {product.description}
              </p>
            </div>

            <div className="space-y-8 mb-8">
              {/* Sizes */}
              {product.sizes?.length > 0 && (
                <div>
                  <label className="block text-sm font-medium text-gray-400 mb-3">SELECT SIZE</label>
                  <div className="flex flex-wrap gap-3">
                    {product.sizes.map((size) => (
                      <button
                        key={size}
                        onClick={() => setSelectedSize(size)}
                        className={`min-w-[3rem] h-12 px-4 rounded-lg flex items-center justify-center border font-medium transition-all ${selectedSize === size
                          ? 'bg-white text-black border-white'
                          : 'bg-transparent border-gray-700 text-gray-300 hover:border-gray-500'
                          }`}
                      >
                        {size}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Colors */}
              {product.colors?.length > 0 && (
                <div>
                  <label className="block text-sm font-medium text-gray-400 mb-3">SELECT COLOR</label>
                  <div className="flex flex-wrap gap-3">
                    {product.colors.map((color) => (
                      <button
                        key={color}
                        onClick={() => setSelectedColor(color)}
                        className={`px-6 py-2 rounded-lg border font-medium transition-all ${selectedColor === color
                          ? 'bg-white text-black border-white'
                          : 'bg-transparent border-gray-700 text-gray-300 hover:border-gray-500'
                          }`}
                      >
                        {color}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Actions */}
            <div className="flex items-center gap-4 mb-8">
              <div className="flex items-center border border-gray-700 rounded-lg h-12">
                <button
                  onClick={() => setQuantity(Math.max(1, quantity - 1))}
                  className="w-12 h-full flex items-center justify-center text-gray-400 hover:text-white"
                >
                  -
                </button>
                <span className="w-8 text-center font-medium">{quantity}</span>
                <button
                  onClick={() => setQuantity(quantity + 1)}
                  className="w-12 h-full flex items-center justify-center text-gray-400 hover:text-white"
                >
                  +
                </button>
              </div>
              <button
                onClick={handleAddToCart}
                disabled={!product.stock}
                className="btn-primary flex-1 flex items-center justify-center gap-2 h-12"
              >
                <FiShoppingBag />
                {product.stock > 0 ? 'ADD TO CART' : 'OUT OF STOCK'}
              </button>
              <button className="h-12 w-12 flex items-center justify-center rounded-lg border border-gray-700 text-gray-400 hover:text-primary-light hover:border-primary-light transition-all">
                <FiHeart />
              </button>
            </div>

            {/* Features / Benefits */}
            <div className="grid grid-cols-2 md:grid-cols-2 gap-4 border-t border-white/10 pt-8 text-sm text-gray-400">
              <div className="flex items-center gap-2">
                <FiTruck className="text-xl" />
                <span>Free delivery above 3000</span>
              </div>
              <div className="flex items-center gap-2">
                <FiRotateCw className="text-xl" />
                <span>30-Day Returns</span>
              </div>
            </div>
          </div>
        </div>

        {/* Product Reviews */}
        <div className="mt-16 md:mt-24 border-t border-white/5 pt-12 md:pt-16">
          <div className="flex flex-col md:flex-row md:items-center justify-between mb-8 md:mb-12 gap-4">
            <div>
              <h2 className="text-2xl md:text-3xl font-display font-bold mb-2 uppercase">Customer Reviews</h2>
              <div className="flex items-center gap-3">
                <div className="flex text-yellow-500 text-sm md:text-xl">
                  {[...Array(5)].map((_, i) => (
                    <span key={i}>
                      {i < Math.round(product.ratings || 5) ? '★' : '☆'}
                    </span>
                  ))}
                </div>
                <span className="text-xs md:text-sm text-gray-400 font-medium">Verified locally in Pakistan</span>
              </div>
            </div>
          </div>

          <div className="grid gap-4 md:gap-8 max-w-4xl">
            {(product.reviews && product.reviews.length > 0 ? product.reviews : []).map((review, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="p-5 md:p-8 rounded-xl md:rounded-2xl bg-white/5 border border-white/10 hover:border-primary-light/30 transition-all group"
              >
                <div className="flex justify-between items-start mb-3 md:mb-4">
                  <div>
                    <h4 className="text-base md:text-lg font-bold text-white group-hover:text-primary-light transition-colors">{review.name}</h4>
                    <p className="text-[10px] md:text-sm text-gray-500 font-medium uppercase tracking-wider">{review.city}, PK</p>
                  </div>
                  <div className="flex text-yellow-500 text-xs md:text-base">
                    {[...Array(5)].map((_, i) => (
                      <span key={i}>
                        {i < review.rating ? '★' : '☆'}
                      </span>
                    ))}
                  </div>
                </div>
                <p className="text-sm md:text-gray-300 italic leading-relaxed text-gray-400">"{review.comment}"</p>
                <p className="text-[10px] text-gray-600 mt-3 md:mt-4 font-mono">
                  {new Date(review.date).toLocaleDateString('en-PK', { day: 'numeric', month: 'short', year: 'numeric' })}
                </p>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Related Products */}
        {relatedProducts.length > 0 && (
          <div className="mt-24 border-t border-white/5 pt-16">
            <h2 className="text-3xl font-display font-bold mb-8">COMPLETE THE LOOK</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {relatedProducts.map((prod) => (
                <ProductCard key={prod._id} product={prod} />
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProductDetail;
